import { features } from 'constants/featureFlags';
import { useParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { rawId } from 'utils/rawId';
import { pathnames, routes } from 'utils/routerHelpers';

import { useEffect } from 'react';

import { useCommunityContext } from 'components/CommunityContext';

export const useHubRedirect = () => {
  const { featureFlags, site } = useCommunityContext();
  const { newCommunities } = site?.features || {};

  const encodedParams = useParams();
  const router = useRouter();

  const params = Object.entries(encodedParams).reduce(
    (acc, [key, value]) => {
      const id = rawId(value as string);
      if (id) {
        acc[key] = id.toString();
      }
      return acc;
    },
    {} as Record<string, string>
  );
  const isNlaDmsEnabled = featureFlags[features.NLA_DMS] || newCommunities?.isEnabled;
  const isNlaCommunitiesEnabled =
    featureFlags[features.NLA_COMMUNITIES] || newCommunities?.isEnabled;
  const isNlaCommunityEventsEnabled = featureFlags[features.NLA_COMMUNITY_EVENTS];
  const PATHNAMES_TO_REDIRECTS = {
    [pathnames.home]: {
      shouldRedirect: isNlaCommunitiesEnabled,
      route: routes.communityHub(params.communityId as string),
    },
    [pathnames.directMessaging.index]: {
      shouldRedirect: isNlaDmsEnabled,
      route: routes.conversationsHub,
    },
    [pathnames.directMessaging.conversation]: {
      shouldRedirect: isNlaDmsEnabled,
      route: routes.goToConversationsHub(params.conversationId as string),
    },
    [pathnames.spaces.space]: {
      shouldRedirect: isNlaCommunitiesEnabled,
      route: routes.spaceHub(params.spaceId as string),
    },
    [pathnames.spaces.index]: {
      shouldRedirect: isNlaCommunitiesEnabled,
      route: routes.spacesHub,
    },
    [pathnames.events.event]: {
      shouldRedirect: isNlaCommunityEventsEnabled && isNlaCommunitiesEnabled,
      route: routes.learnerHubEvent(params.eventId as string),
    },
    [pathnames.events.events]: {
      shouldRedirect: isNlaCommunityEventsEnabled && isNlaCommunitiesEnabled,
      route: routes.learnerHubEvents(),
    },
  };

  useEffect(() => {
    // if in iframe, don't redirect
    if (window?.self !== window?.top) return;
    const pathNameDeclaration = PATHNAMES_TO_REDIRECTS[router.pathname];
    if (pathNameDeclaration?.shouldRedirect) {
      window.location.href = pathNameDeclaration.route;
    }
  }, [router.pathname, PATHNAMES_TO_REDIRECTS]);

  return {
    isNlaDmsEnabled,
    isNlaCommunitiesEnabled,
  };
};
